import { Badge, Box, Checkbox, Flex, HStack, IconButton, useToast, useToken } from "@chakra-ui/react";
import { useFormik } from "formik";
import { useCallback, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import Select from "react-select";
import * as Yup from "yup";

import { isPostalCodeExcludedCountry } from "@equidefi/constants/countries";
import {
  ENTITY_SUB_TYPE_OPTIONS,
  JOINT_SUB_TYPE_OPTIONS,
  TRUST_SUB_TYPE_OPTIONS,
  US_CITIZEN,
} from "@equidefi/constants/profile";
import { ASCII_ONLY_REGEX } from "@equidefi/constants/validation";
import { useDisclosure } from "@equidefi/hooks";
import { extractErrorMessage } from "@equidefi/portals";
import { SignerModal } from "@equidefi/portals/components/SignerModal";
import { useDocumentTitle } from "@equidefi/portals/hooks/useDocumentTitle";
import { useInvestmentSigners } from "@equidefi/portals/hooks/useSigners";
import {
  useDisconnectVaultRecord,
  useGetInvestmentVaults,
  useSaveVaultEntity,
  useUpdateVault,
} from "@equidefi/portals/hooks/useVault";
import { capitalizeEveryWord, userFullName } from "@equidefi/shared";
import { BannerAlert, CardList, CardListItem, ConfirmModal, Heading, Text } from "@equidefi/ui";
import { Input, MaskedInput } from "@equidefi/ui/forms";
import { Icon } from "@equidefi/ui/icon";

import WorkflowProtection from "@/components/workflow/WorkflowProtection";
import useWorkflowPosition from "@/hooks/useWorkflowPosition";
import { useWorkflowContext } from "@/pages/workflow/context/WorkflowContext";

import { MESSAGE } from "../../../../../issuer/src/constants/forms";

import { WorkflowContainer } from "../WorkflowContainer";
import WorkflowForm from "../WorkflowForm";
import { EntityAddressForm } from "../address/AddressForm";

const entityAddressSchema = (fieldName) =>
  Yup.string()
    .nullable()
    .test("address-required-for-entity-or-trust", function (value) {
      const { account_type } = this.parent;
      if ((account_type === "ENTITY" || account_type === "TRUST") && !value?.trim()) {
        return this.createError({
          message: MESSAGE.REQUIRED(fieldName),
        });
      }
      return true;
    });

// Postal code is required for entities/trusts like the other address fields, unless the
// selected country doesn't use postal codes — then it's hidden and optional.
const entityPostalCodeSchema = Yup.string()
  .nullable()
  .test("postal-required-for-entity-or-trust", function (value) {
    const { account_type, entity_address_country_code } = this.parent;
    const isEntityOrTrust = account_type === "ENTITY" || account_type === "TRUST";
    if (isEntityOrTrust && !isPostalCodeExcludedCountry(entity_address_country_code) && !value?.trim()) {
      return this.createError({
        message: MESSAGE.REQUIRED("Postal code"),
      });
    }
    return true;
  });

export const entityTypeSchema = Yup.object().shape({
  company: Yup.string()
    .nullable()
    .matches(ASCII_ONLY_REGEX, MESSAGE.ASCII_ONLY)
    .test("company-required-for-entity-or-trust", function (value) {
      const { account_type } = this.parent;
      if ((account_type === "ENTITY" || account_type === "TRUST") && !value?.trim()) {
        return this.createError({
          message: account_type === "TRUST" ? MESSAGE.REQUIRED("Trust Name") : MESSAGE.REQUIRED("Entity Name"),
        });
      }
      return true;
    }),
  tax_id: Yup.string()
    .nullable()
    .test("ssn-required", function (value) {
      const { citizenship } = this.parent;
      if (!value?.trim()) {
        return this.createError({
          message:
            citizenship === US_CITIZEN
              ? MESSAGE.REQUIRED("Social Security or Tax ID")
              : MESSAGE.REQUIRED("Tax ID or Government Issued ID"),
        });
      }

      if (citizenship === US_CITIZEN && !/^\d{9}$/.test(value.trim())) {
        return this.createError({
          message: "Must be exactly 9 digits, numbers only (no dashes)",
        });
      }

      if (citizenship !== US_CITIZEN && value.trim().length > 20) {
        return this.createError({
          message: "Must not exceed 20 characters",
        });
      }

      return true;
    }),
  ein: Yup.string()
    .nullable()
    .test("ein-required-for-entity-or-trust", function (value) {
      const { account_type, citizenship } = this.parent;
      if (account_type === "ENTITY" || account_type === "TRUST") {
        if (!value?.trim()) {
          return this.createError({
            message: MESSAGE.REQUIRED(),
          });
        }
        if (citizenship !== US_CITIZEN && value.trim().length > 20) {
          return this.createError({
            message: "Must not exceed 20 characters",
          });
        }
        if (citizenship === US_CITIZEN && !/^\d{9}$/.test(value.trim())) {
          return this.createError({
            message: "Must be exactly 9 digits, numbers only (no dashes)",
          });
        }
      }
      return true;
    }),
  ta_sub_type: Yup.string()
    .nullable()
    .test("individual-sub-type-required-if-joint", function (value) {
      const { account_type } = this.parent;
      if ((account_type === "ENTITY" || account_type === "TRUST" || account_type === "JOINT") && !value?.trim()) {
        return this.createError({
          message: MESSAGE.REQUIRED("Joint Type"),
        });
      }
      return true;
    }),
  entity_address_street_1: entityAddressSchema("Street"),
  entity_address_street_2: Yup.string().nullable(),
  entity_address_city: entityAddressSchema("City"),
  entity_address_state: entityAddressSchema("State"),
  entity_address_postal_code: entityPostalCodeSchema,
  entity_address_country_code: entityAddressSchema("Country code"),
});

export const WorkflowSigners = () => {
  const { investment, offering, user, refetchInvestment } = useWorkflowContext();
  useWorkflowPosition(5);
  const [equidefiRed, equidefiBlue] = useToken("colors", ["equidefi.red", "equidefi.blue"]);
  const toast = useToast();
  const location = useLocation();
  const [spinner, showSpinner] = useState(false);
  const navigate = useNavigate();
  const [disableEIN, setDisableEIN] = useState(false);
  useDocumentTitle(["Signers", offering?.name, investment?.issuer?.name]);

  const entityUpdate = useSaveVaultEntity(investment?.id);
  const modal = useDisclosure();

  const [entityAddressDisabled, setEntityAddressDisabled] = useState(false);

  const onSubmit = async (data) => {
    showSpinner(true);
    let page = "kyc";

    if (location.state?.returnTo) {
      page = location.state.returnTo;
    }

    const payload = {
      company: data.company,
      ta_sub_type: data.ta_sub_type?.value,
      tax_id: data.tax_id,
      birthdate: data.birthdate,
      ein: data.ein,
      user_id: user.id,
      entity_address_street_1: data.entity_address_street_1,
      entity_address_street_2: data.entity_address_street_2,
      entity_address_city: data.entity_address_city,
      entity_address_state: data.entity_address_state,
      entity_address_postal_code: data.entity_address_postal_code,
      entity_address_country_code: data.entity_address_country_code,
      skip_transition: location.state?.skip_transition,
    };

    if (investment.vault?.account_type === "INDIVIDUAL" || investment.vault?.account_type === "JOINT") {
      payload.company = undefined;
      payload.ein = undefined;
    }

    try {
      await entityUpdate.mutateAsync(payload);

      await refetchInvestment();

      navigate(`/offerings/${offering.slug}/${investment?.id}/${page}`, {
        state: {
          refresh: true,
        },
      });
    } catch (error) {
      console.error(error);
      toast({
        status: "error",
        description: error.response?.data?.errors?.join(". ") ?? "Something went wrong",
      });
    } finally {
      showSpinner(false);
    }
  };

  const subTypeOptions = useMemo(() => {
    switch (investment?.vault?.account_type) {
      case "ENTITY":
        return ENTITY_SUB_TYPE_OPTIONS;
      case "TRUST":
        return TRUST_SUB_TYPE_OPTIONS;
      case "JOINT":
        return JOINT_SUB_TYPE_OPTIONS;
      default:
        return [];
    }
  }, [investment?.vault?.account_type]);

  const {
    handleSubmit,
    setFieldValue,
    getFieldProps,
    values,
    errors,
    touched,
    isValid,
    setValues,
    setFieldTouched,
    validateForm,
  } = useFormik({
    initialValues: {
      company: investment.vault.company,
      ta_sub_type: subTypeOptions.find((option) => option.value === investment.vault.ta_sub_type),
      ein: investment.vault.ein,
      tax_id: investment.vault.tax_id,
      birthdate: investment.vault.birthdate,
      account_type: investment.vault.account_type,
      citizenship: investment.vault.citizenship,
      entity_address_street_1: investment.vault.entity_address_street_1 ?? "",
      entity_address_street_2: investment.vault.entity_address_street_2 ?? "",
      entity_address_city: investment.vault.entity_address_city ?? "",
      entity_address_state: investment.vault.entity_address_state ?? "",
      entity_address_postal_code: investment.vault.entity_address_postal_code ?? "",
      entity_address_country_code: investment.vault.entity_address_country_code ?? "",
    },
    enableReinitialize: true,
    validationSchema: entityTypeSchema,
    onSubmit,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialTouched: {
      ein: !!investment.vault.ein,
      tax_id: !!investment.vault.tax_id,
      birthdate: !!investment.vault.birthdate,
    },
  });

  const {
    nonPrimaryVaults,
    onEdit,
    onCreate,
    onSubmit: onSubmitModal,
    canAddMoreSigners,
    minimumSigners,
    initialValues,
    refetchSignatures,
    canAddSigner,
    canAddUBO,
  } = useInvestmentSigners(investment?.id, investment.vault?.account_type, {
    onSuccess: () => {
      refetchSignatures();
      refetchVaults();
      modal.onClose();
    },
  });

  const { data: vaultRecords, refetch: refetchVaults } = useGetInvestmentVaults(investment?.id);
  const disconnectVaultRecord = useDisconnectVaultRecord(investment?.id);
  const [vaultRecordToRemove, setVaultRecordToRemove] = useState(null);
  const [editingVaultId, setEditingVaultId] = useState(null);
  const [editingVaultRecordId, setEditingVaultRecordId] = useState(null);
  const updateVault = useUpdateVault(editingVaultId, investment?.id);

  const handleModalSubmit = async (data) => {
    if (initialValues?.email) {
      try {
        await updateVault.mutateAsync(data);
        toast({ status: "success", description: "Successfully saved signer" });
        setEditingVaultId(null);
        await refetchSignatures();
        await refetchVaults();
        modal.onClose();
      } catch (e) {
        console.error(e);
        toast({
          status: "error",
          description: e.response?.data?.errors?.join(". ") ?? "Something went wrong",
        });
      }
    } else {
      await onSubmitModal(data);
    }
  };

  const continueButtonLabel = useMemo(
    () =>
      nonPrimaryVaults.length > 0 || investment?.vault?.entity_type === "INDIVIDUAL"
        ? "Continue"
        : "Skip without additional Signers",
    [nonPrimaryVaults, investment?.vault?.entity_type],
  );

  const handleOnAddressSelect = useCallback(
    (value) => {
      const newValues = {
        ...values,
        entity_address_street_1: value.street,
        entity_address_street_2: value?.street2,
        entity_address_city: value.city,
        entity_address_state: value.state,
        entity_address_postal_code: value.zipcode,
        entity_address_country_code: value.country,
      };

      setValues(newValues);

      // Trigger validation for all fields
      for (const field of Object.keys(newValues)) {
        setFieldTouched(field, true, false);
      }

      // Run validation
      validateForm(newValues);
    },
    [values, setValues, setFieldTouched, validateForm],
  );

  const handleAddressCheckboxChange = (e) => {
    if (e.target.checked) {
      setValues({
        ...values,
        entity_address_street_1: investment.vault.address_street_1,
        entity_address_street_2: investment.vault.address_street_2,
        entity_address_city: investment.vault.address_city,
        entity_address_state: investment.vault.address_state,
        entity_address_postal_code: investment.vault.address_postal_code,
        entity_address_country_code: investment.vault.address_country_code,
      });
      setEntityAddressDisabled(true);
    } else {
      setValues({
        ...values,
        entity_address_street_1: null,
        entity_address_street_2: null,
        entity_address_city: null,
        entity_address_state: null,
        entity_address_postal_code: null,
        entity_address_country_code: null,
      });
      setEntityAddressDisabled(false);
    }
  };

  // Determine if investor is a US citizen for conditional copy
  const isUSCitizen = values.citizenship === US_CITIZEN;
  const isEntity = investment.vault?.entity_type === "ENTITY";
  const taxIdLabel = isUSCitizen ? "SSN/TIN" : "Government Issued ID Number";

  return (
    <WorkflowContainer
      title="Investment Details"
      onContinue={handleSubmit}
      buttonLabel={continueButtonLabel}
      buttonProps={{
        isDisabled: !isValid || !minimumSigners,
        isLoading: spinner,
      }}>
      <WorkflowProtection vaultId={investment?.vault_id} />
      <WorkflowForm onSubmit={handleSubmit}>
        <BannerAlert
          status="info"
          description={
            isUSCitizen ? "Please enter your SSN or Tax ID Number." : "Please enter your Government Issued ID Number"
          }
          mt="4"
        />
        <Box mt={5}>
          <MaskedInput
            label={investment?.vault?.entity_type === "INDIVIDUAL" ? taxIdLabel : `Authorized Signer's ${taxIdLabel}`}
            isRequired
            maxLength={isUSCitizen ? 9 : 20}
            error={touched.tax_id && errors.tax_id}
            isInvalid={errors.tax_id && touched.tax_id}
            fieldType={taxIdLabel}
            autoFocus
            {...getFieldProps("tax_id")}
          />
        </Box>

        {investment.vault?.account_type === "TRUST" && (
          <Heading textStyle="h1" m={0} mb={0} mt={10} fontWeight="700">
            Trust Details
          </Heading>
        )}
        {investment.vault?.account_type === "ENTITY" && (
          <Heading textStyle="h1" m={0} mb={0} mt={10} fontWeight="700">
            Entity Details
          </Heading>
        )}
        <Flex mb={5} />
        {investment?.vault?.account_type === "ENTITY" && (
          <Flex flexDir="column" mb={5}>
            <Text>
              Select Entity Type <span style={{ color: "red" }}>*</span>
            </Text>
            <Select
              value={values.ta_sub_type}
              onChange={(value) => setFieldValue("ta_sub_type", value)}
              options={ENTITY_SUB_TYPE_OPTIONS}
              components={{
                IndicatorSeparator: null,
              }}
            />
          </Flex>
        )}

        {investment?.vault?.account_type === "TRUST" && (
          <Flex flexDir="column" mb={5}>
            <Text>
              Select Trust Type <span style={{ color: "red" }}>*</span>
            </Text>
            <Select
              value={values.ta_sub_type}
              onChange={(value) => setFieldValue("ta_sub_type", value)}
              options={TRUST_SUB_TYPE_OPTIONS}
              components={{
                IndicatorSeparator: null,
              }}
            />
          </Flex>
        )}

        {(investment?.vault?.account_type === "ENTITY" || investment?.vault?.account_type === "TRUST") && (
          <>
            <Input
              label={`${capitalizeEveryWord(investment?.vault?.account_type)} Name`}
              placeholder={
                investment?.vault?.account_type === "ENTITY"
                  ? "e.g. Acme Corporation"
                  : "John Doe and Jane Doe Revocable Trust dated 10/11/2020"
              }
              isRequired
              error={touched.company && errors.company}
              isInvalid={errors.company && touched.company}
              {...getFieldProps("company")}
            />
            <Checkbox
              name="copySSN"
              onChange={(e) => {
                setFieldValue("copySSN", e.target.checked);
                setDisableEIN(e.target.checked);
                if (e.target.checked) {
                  setFieldTouched("ein", true);
                  setFieldValue("ein", values.tax_id, true);
                }
              }}
              isChecked={values.copySSN}>
              My {taxIdLabel} is my {capitalizeEveryWord(investment?.vault?.account_type)}'s Tax ID number
            </Checkbox>
          </>
        )}

        {investment?.vault?.account_type === "JOINT" && (
          <Flex flexDir="column" mb={10}>
            <Text>
              Select Joint Type <span style={{ color: "red" }}>*</span>
            </Text>
            <Select
              value={values.ta_sub_type}
              onChange={(value) => setFieldValue("ta_sub_type", value)}
              options={JOINT_SUB_TYPE_OPTIONS}
              components={{
                IndicatorSeparator: null,
              }}
            />
          </Flex>
        )}

        {(investment?.vault?.account_type === "ENTITY" || investment?.vault?.account_type === "TRUST") && (
          <Box mt={5}>
            <BannerAlert
              status="info"
              description={`Please enter your ${
                investment?.vault?.account_type === "ENTITY" ? "EIN" : "Tax ID Number (TIN)"
              } without dashes.`}
              mb="4"
            />
            <MaskedInput
              label={investment.vault?.account_type === "ENTITY" ? "EIN" : "Tax ID Number (TIN)"}
              isRequired
              isDisabled={disableEIN}
              maxLength={!isUSCitizen && isEntity ? 20 : 9}
              error={touched.ein && errors.ein}
              isInvalid={touched.ein && errors.ein}
              fieldType={investment.vault?.account_type === "ENTITY" ? "EIN" : "TIN"}
              {...getFieldProps("ein")}
            />

            <Heading textStyle="h2" mb={0} mt={10} fontWeight="bold">
              Entity address
            </Heading>
            <Text mb="2">
              Enter the legal address of the {investment.vault?.account_type?.toLowerCase() ?? "entity"}, as shown in
              your {investment.vault?.account_type?.toLowerCase() ?? "entity"} documents.
            </Text>
            <Checkbox size="md" mb="4" onChange={handleAddressCheckboxChange} isChecked={entityAddressDisabled}>
              Use the previously entered address
            </Checkbox>
            {!entityAddressDisabled && (
              <EntityAddressForm
                values={values}
                getFieldProps={getFieldProps}
                setFieldValue={setFieldValue}
                errors={errors}
                touched={touched}
                onAddressSelect={handleOnAddressSelect}
              />
            )}
          </Box>
        )}
      </WorkflowForm>
      <Heading textStyle="h1" m={0} mb={0} mt={10} fontWeight="700">
        {investment.vault?.account_type === "INDIVIDUAL" ? "Individuals" : "Additional Individuals"}
      </Heading>
      <Text mb={3}>
        {investment.vault?.account_type === "JOINT" ? "You must add your joint signer before proceeding" : null}
      </Text>
      {investment.vault?.entity_type === "ENTITY" && (
        <Text>You can add additional signers/UBO's to the investment.</Text>
      )}
      <CardList my="4">
        <CardListItem>
          <Heading my="2">{userFullName(investment?.vault)}</Heading>
          <Text textStyle="context" m="0" isTruncated>
            {user?.email}
          </Text>
          <Text textStyle="context" mb="2" isTruncated>
            {investment?.vault?.phone_number}
          </Text>
          <HStack spacing="4" mt={5} mb={3}>
            <Badge>SIGNER</Badge>
            <Badge>KYC</Badge>
          </HStack>
        </CardListItem>
        {investment.vault?.account_type !== "INDIVIDUAL" &&
          nonPrimaryVaults.map((value, i) => (
            <CardListItem
              key={value.id || i}
              action={
                <IconButton
                  onClick={() => setVaultRecordToRemove(value.id)}
                  icon={<Icon.Trash size="1.5em" color={equidefiRed} />}
                />
              }
              secondaryAction={
                <IconButton
                  onClick={() => {
                    setEditingVaultId(value.vault?.id);
                    setEditingVaultRecordId(value.id);
                    onEdit({
                      first_name: value.vault?.first_name,
                      middle_name: value.vault?.middle_name,
                      last_name: value.vault?.last_name,
                      email: value.email,
                      phone_number: value.vault?.phone_number,
                      is_kyc: value.vault?.is_kyc,
                      is_signer: value.vault?.is_signer,
                    });
                    modal.onOpen();
                  }}
                  icon={<Icon.Edit size="1.5em" color={equidefiBlue} />}
                />
              }>
              <Heading my="2">{userFullName(value.vault)}</Heading>
              <Text textStyle="context" m="0" isTruncated>
                {value.email}
              </Text>
              <Text textStyle="context" mb="2" isTruncated>
                {value.vault.phone_number}
              </Text>
              <HStack spacing="4" mt={5} mb={3}>
                {value.vault?.is_signer && <Badge>SIGNER</Badge>}
                {value.vault?.is_kyc && <Badge>KYC</Badge>}
              </HStack>
            </CardListItem>
          ))}
        {canAddMoreSigners && (
          <CardListItem
            key="add"
            onClick={() => {
              onCreate();
              modal.onOpen();
            }}
            action={<IconButton icon={<Icon.Plus size="1.5em" />} />}>
            <Text textStyle="context" my="2">
              Add Another Individual
            </Text>
          </CardListItem>
        )}
      </CardList>
      {modal.isOpen && (
        <SignerModal
          canAddUBO={canAddUBO}
          canAddSigner={canAddSigner}
          isOpen={modal.isOpen}
          initialValues={initialValues}
          onHide={modal.onClose}
          onSubmit={handleModalSubmit}
          onDelete={() => {
            modal.onClose();
            setVaultRecordToRemove(editingVaultRecordId);
          }}
          investment={investment}
        />
      )}

      <ConfirmModal
        isOpen={!!vaultRecordToRemove}
        title="Remove Individual"
        description="Are you sure you want to remove this individual?"
        confirmText="Yes"
        cancelText="Cancel"
        confirmButtonColor="red"
        onConfirm={async () => {
          if (!vaultRecordToRemove) return;
          try {
            await disconnectVaultRecord.mutateAsync(vaultRecordToRemove);
            toast({ status: "success", description: "Successfully removed signer" });
            setVaultRecordToRemove(null);
            await refetchSignatures();
            await refetchVaults();
          } catch (e) {
            console.error(e);
            toast({
              status: "error",
              description: extractErrorMessage(e),
            });
          }
        }}
        onCancel={() => setVaultRecordToRemove(null)}
      />
    </WorkflowContainer>
  );
};
